import { compareByString, isPresent, throwErrorUnlessProduction, uniq } from '@luminovo/commons';
import {
    ApprovalStatus,
    CustomFullPart,
    CustomLinkedPartDTO,
    CustomOptionOfferDTO,
    CustomOptionTypes,
    CustomOptionVariantDTO,
    CustomPartTypeEnum,
    DesignItemResponseDTO,
    FullPart,
    PackageTag,
    PartCategoryDTO,
    PartSpecification,
    RfQContextQueryParams,
    RfqContext,
    StandardPartDTO,
    StandardPartOfferDTO,
    StandardPartTypes,
    getApprovedCustomPartIds,
    getApprovedGenericPartIds,
    getApprovedIpnIds,
    getApprovedOtsPartIds,
    getCustomApprovedIpnIds,
    isCustomComponentFull,
    isCustomFullPart,
    isGenericFullPart,
    isOtsComponentFull,
    isOtsFullPart,
} from '@luminovo/http-client';
import {
    isCustomComponentOffer,
    isCustomPartOffer,
    isInternalPartNumberOffer,
    isOffTheShelfPartOffer,
} from '@luminovo/sourcing-core';
import { UseQueryResult } from '@tanstack/react-query';
import React from 'react';
import { getPartIdsSet } from '../../utils/getPartIds';
import { assertUnreachable } from '../../utils/typingUtils';
import { useDesignItems } from '../designItem/designItemHandler';
import { useHttpQuery, useSuspenseHttpQuery } from '../http/useHttpQuery';
import { usePcb } from '../pcb/pcbHandlers';
import { useSolutionConfiguration } from '../solutionConfiguration/solutionConfigurationHandler';
import { PartSpecificationTypes } from './PartSpecificationTypes';
import { FullPartWithApprovalStatus } from './partFrontendTypes';

export function convertRfqContext(rfqContext: RfqContext): RfQContextQueryParams {
    if (rfqContext.type === 'WithinRfQ') {
        return {
            rfq_context: rfqContext.type,
            rfq_id: rfqContext.rfq_id,
        };
    }
    if (rfqContext.type === 'OutsideRfQ') {
        return {
            rfq_context: rfqContext.type,
        };
    }
    assertUnreachable(rfqContext);
}

export function usePartCategory(id: number | undefined) {
    const { data, isLoading } = usePartCategories();
    return { data: data?.find((category) => category.id === id), isLoading };
}

export function usePartCategories() {
    return useSuspenseHttpQuery(
        'GET /parts/part-categories',
        {},
        {
            select: (data) => {
                const partCategories = data.items;

                const childrenToParentMap = new Map(
                    partCategories.flatMap((category) => category.children.map((child) => [child, category])),
                );

                const buildPath = (category: PartCategoryDTO): PartCategoryDTO[] => {
                    if (category.depth === 0) {
                        return [category];
                    }

                    const parentCategory = childrenToParentMap.get(category.id);
                    const parentPath = parentCategory ? buildPath(parentCategory) : [];

                    return [...parentPath, category];
                };

                return partCategories.map((partCategory) => ({
                    ...partCategory,
                    path: buildPath(partCategory),
                }));
            },
            staleTime: Infinity,
        },
    );
}

export function useStandardPartsFromOffers(offers: StandardPartOfferDTO[] | undefined, rfqContext: RfqContext) {
    const otsPartIds = React.useMemo(() => {
        return offers?.filter((offer) => isOffTheShelfPartOffer(offer)).map((offer) => offer.linked_part.id);
    }, [offers]);
    const ipnIds = React.useMemo(() => {
        return offers?.filter((offer) => isInternalPartNumberOffer(offer)).map((offer) => offer.linked_part.id);
    }, [offers]);

    const { data: otsParts, isLoading: isLoadingOtsParts } = useOtsPartBulk(otsPartIds, rfqContext);
    const { data: ipns, isLoading: isLoadingIpns } = useIpnBulk(ipnIds, rfqContext);

    return React.useMemo(() => {
        if (isLoadingOtsParts || isLoadingIpns) {
            return { data: undefined, ipns: undefined, otsParts: undefined, isLoading: true };
        }

        return { data: [...(otsParts ?? []), ...(ipns ?? [])], ipns, otsParts, isLoading: false };
    }, [isLoadingOtsParts, isLoadingIpns, otsParts, ipns]);
}

export function useCustomPartsFromOffers(offers: CustomOptionOfferDTO[] | undefined, rfqContext: RfqContext) {
    const customPartIds = React.useMemo(() => {
        return offers?.filter((offer) => isCustomPartOffer(offer)).map((offer) => offer.linked_part.id);
    }, [offers]);

    const ipnIds = React.useMemo(() => {
        return offers?.filter((offer) => isCustomComponentOffer(offer)).map((offer) => offer.linked_part.id);
    }, [offers]);

    const { data: customParts, isLoading: isLoadingCustomParts } = useCustomPartsBulk(customPartIds);
    const { data: ipns, isLoading: isLoadingIpns } = useCustomComponentsBulk(ipnIds, rfqContext);

    return React.useMemo(() => {
        if (isLoadingCustomParts || isLoadingIpns) {
            return { data: undefined, ipns: undefined, otsParts: undefined, isLoading: true };
        }

        return { data: [...(customParts ?? []), ...(ipns ?? [])], ipns, customParts, isLoading: false };
    }, [isLoadingCustomParts, isLoadingIpns, customParts, ipns]);
}

export function useOtsFullPart({
    partOptionId,
    rfqContext,
}: {
    partOptionId: string | undefined;
    rfqContext: RfqContext;
}) {
    return useHttpQuery(
        'POST /parts/off-the-shelf/bulk',
        {
            requestBody: { ids: [partOptionId!], ...convertRfqContext(rfqContext) },
        },
        {
            select: (res) => res.items[0],
            enabled: !!partOptionId,
        },
    );
}

export function useOtsPartBulk(ids: string[] | undefined, rfqContext: RfqContext) {
    return useHttpQuery(
        'POST /parts/off-the-shelf/bulk',
        {
            requestBody: { ids: ids!, ...convertRfqContext(rfqContext) },
        },
        {
            select: (res) => res.items,
            enabled: !!ids,
            meta: { globalErrorHandler: false },
        },
    );
}

export function useIpn(
    id: string | undefined,
    rfqContext: RfqContext,
    queryOptions?: { refetchOnWindowFocus: boolean },
) {
    return useHttpQuery(
        'POST /ipns/bulk',
        {
            requestBody: { ids: [id!], ...convertRfqContext(rfqContext) },
        },
        {
            select: (res) => res.items[0],
            enabled: !!id,
            ...queryOptions,
        },
    );
}

export function useIpnBulk(ids: string[] | undefined, rfqContext: RfqContext) {
    return useHttpQuery(
        'POST /ipns/bulk',
        {
            requestBody: { ids: ids!, ...convertRfqContext(rfqContext) },
        },
        {
            select: (res) => res.items,
            enabled: !!ids,
        },
    );
}

export function useComponentsBulk(ids: string[] | undefined, rfqContext: RfqContext) {
    return useHttpQuery(
        'POST /components/bulk',
        {
            requestBody: { ids: ids!, ...convertRfqContext(rfqContext) },
        },
        {
            select: (res) => res.items,
            enabled: !!ids,
        },
    );
}

export function useComponent(
    id: string | undefined,
    rfqContext: RfqContext,
    queryOptions?: { refetchOnWindowFocus: boolean },
) {
    return useHttpQuery(
        'POST /components/bulk',
        {
            requestBody: { ids: [id!], ...convertRfqContext(rfqContext) },
        },
        {
            select: (res) => res.items[0],
            enabled: !!id,
            ...queryOptions,
        },
    );
}

export function useFullStandardPartsBulk(
    standardParts: StandardPartDTO[],
    rfqId: string | undefined,
): { isLoading: boolean; fullParts: FullPart[]; isFetched: boolean } {
    const context = rfqId ? ({ rfq_id: rfqId, type: 'WithinRfQ' } as const) : ({ type: 'OutsideRfQ' } as const);
    const otsPartIds = React.useMemo(() => {
        return uniq(standardParts.filter((p) => p.type === StandardPartTypes.OffTheShelf).map((p) => p.data));
    }, [standardParts]);

    const {
        data: otsPartsResponse,
        isLoading: isOtsPartsLoading,
        isFetched: isOtsPartsFetched,
    } = useOtsPartBulk(otsPartIds, context);
    const genericPartIds = React.useMemo(() => {
        return uniq(standardParts.filter((p) => p.type === StandardPartTypes.Generic).map((p) => p.data));
    }, [standardParts]);

    const {
        data: genericPartsResponse,
        isLoading: isGenericPartsLoading,
        isFetched: isGenericPartsFetched,
    } = useGenericPartBulk(genericPartIds, context);

    const ipnIds = React.useMemo(() => {
        return uniq(standardParts.filter((p) => p.type === StandardPartTypes.Ipn).map((p) => p.data));
    }, [standardParts]);

    const { data: ipnResponse, isLoading: isIpnLoading, isFetched: isIpnFetched } = useIpnBulk(ipnIds, context);

    const fullParts = React.useMemo(() => {
        const otsParts: FullPart[] = otsPartsResponse ?? [];
        const genericParts: FullPart[] = genericPartsResponse ?? [];
        const ipns: FullPart[] = ipnResponse ?? [];
        return otsParts.concat(genericParts).concat(ipns);
    }, [otsPartsResponse, genericPartsResponse, ipnResponse]);

    const isFetched = isOtsPartsFetched && isIpnFetched && isGenericPartsFetched;

    return {
        isLoading: isOtsPartsLoading || isGenericPartsLoading || isIpnLoading,
        isFetched,
        fullParts,
    };
}

export function useFullCustomPartsBulk(
    customParts: CustomOptionVariantDTO[],
    rfqId: string | undefined,
): { isLoading: boolean; customParts: FullPart[]; isFetched: boolean } {
    const context = rfqId ? ({ rfq_id: rfqId, type: 'WithinRfQ' } as const) : ({ type: 'OutsideRfQ' } as const);
    const customPartIds = React.useMemo(() => {
        return uniq(customParts.filter((p) => p.type === CustomOptionTypes.CustomPart).map((p) => p.data));
    }, [customParts]);

    const {
        data: customPartsResponse,
        isLoading: isCustomPartsLoading,
        isFetched: isCustomPartsFetched,
    } = useCustomPartsBulk(customPartIds);

    const customComponentIds = React.useMemo(() => {
        return uniq(customParts.filter((p) => p.type === CustomOptionTypes.CustomComponent).map((p) => p.data));
    }, [customParts]);

    const {
        data: customComponentsResponse,
        isLoading: isCustomComponentsLoading,
        isFetched: isCustomComponentsFetched,
    } = useCustomComponentsBulk(customComponentIds, context);

    const allCustomParts = React.useMemo(() => {
        const customParts: FullPart[] = customPartsResponse ?? [];
        const customComponents: FullPart[] = customComponentsResponse ?? [];
        return customParts.concat(customComponents);
    }, [customPartsResponse, customComponentsResponse]);

    const isLoading = isCustomPartsLoading || isCustomComponentsLoading;
    const isFetched = isCustomPartsFetched && isCustomComponentsFetched;

    return {
        isLoading,
        isFetched,
        customParts: allCustomParts,
    };
}

function useGenericPartBulk(ids: string[], rfqContext: RfqContext) {
    return useHttpQuery(
        'POST /parts/generic/bulk',
        {
            requestBody: { ids: ids!, ...convertRfqContext(rfqContext) },
        },
        {
            select: (res) => res.items,
            enabled: !!ids,
        },
    );
}

export const useFirstLinkedPartFromComponent = (
    linkedPart: CustomLinkedPartDTO | undefined,
): CustomFullPart | undefined => {
    // For CustomPart, use the existing hook
    const { data: customPartData } = useCustomPart(linkedPart?.type === 'CustomPart' ? linkedPart.id : undefined);

    // For CustomComponent, use the direct query
    const { data: componentData } = useHttpQuery(
        'GET /components/custom/:componentId',
        {
            pathParams: { componentId: linkedPart?.type === 'CustomComponent' ? linkedPart.id : '' },
        },
        {
            enabled: linkedPart?.type === 'CustomComponent',
            select: (responseData) => {
                return responseData.matches[0];
            },
        },
    );

    // Return the appropriate data based on the linkedPart type
    if (linkedPart?.type === 'CustomPart') {
        return customPartData;
    } else if (linkedPart?.type === 'CustomComponent') {
        return componentData;
    }

    return undefined;
};

export function useCustomPart(partId: string | undefined = ''): UseQueryResult<CustomFullPart> {
    return useHttpQuery(
        'GET /parts/custom/:partId',
        { pathParams: { partId } },
        { enabled: Boolean(partId), select: (res) => res.data },
    );
}

export function useCustomPartResources(partId: string | undefined = '') {
    return useHttpQuery(
        'GET /parts/custom/:partId/resources',
        { pathParams: { partId } },
        { enabled: Boolean(partId), select: (res) => res.items },
    );
}

export function useCustomPartGetUploadFilesUrl(partId: string) {
    return useHttpQuery(
        'GET /parts/custom/:partId/upload',
        { pathParams: { partId } },
        {
            select: (data) => data.data.url,
        },
    );
}

export function useCustomPartsBulk(ids: string[] | undefined) {
    return useHttpQuery(
        'POST /parts/custom/bulk',
        {
            requestBody: { ids: ids! },
        },
        {
            select: (res) => res.items,
            enabled: !!ids,
        },
    );
}

export function useCustomComponentsBulk(ids: string[] | undefined, rfqContext: RfqContext) {
    return useHttpQuery(
        'POST /components/custom/bulk',
        {
            requestBody: { ids: ids!, ...convertRfqContext(rfqContext) },
        },
        {
            select: (res) => res.items,
            enabled: !!ids,
        },
    );
}
export function useCustomComponent(
    id: string,
    rfqContext: RfqContext,
    queryOptions?: { refetchOnWindowFocus: boolean },
) {
    return useHttpQuery(
        'POST /components/custom/bulk',
        {
            requestBody: { ids: [id], ...convertRfqContext(rfqContext) },
        },
        {
            select: (res) => res.items[0],
            enabled: !!id,
            ...queryOptions,
        },
    );
}

export function resolvePartOptions(part: FullPart | undefined): FullPart[] {
    if (!part) {
        return [];
    }
    if (isOtsFullPart(part)) {
        return [part];
    }
    if (isGenericFullPart(part)) {
        return part.matches;
    }
    if (isOtsComponentFull(part)) {
        return part.matches.flatMap((match) => resolvePartOptions(match.part.data));
    }
    if (isCustomFullPart(part)) {
        return [part];
    }
    if (isCustomComponentFull(part)) {
        return part.matches.flatMap((part) => resolvePartOptions(part));
    }
    assertUnreachable(part);
}

export const usePartOptionsFromSolutionConfigurationId = ({
    solutionConfigurationId,
    rfqId,
}: {
    solutionConfigurationId: string;
    rfqId: string;
}) => {
    const { data: solutionConfiguration } = useSolutionConfiguration(solutionConfigurationId);
    const { data: designItems } = useDesignItems(solutionConfiguration?.bom_items.design_items.items);

    return useApprovedParts({
        designItems,
        rfqContext: { rfq_id: rfqId, type: 'WithinRfQ' },
    });
};

/**
 * Fetches all APPROVED parts that satisfy the given part specifications.
 *
 * @param rfqContext The RfQ this is called from. Needed to filter IPN / CPN data.
 * @returns Returns a list of all parts that satisfy the given part specification. Notice that full parts will only be returned whenever the part specification's approval status is Approved.
 */
export function useApprovedParts({
    designItems,
    rfqContext,
}: {
    designItems: DesignItemResponseDTO[] | undefined;
    rfqContext: {
        type: 'WithinRfQ';
        rfq_id: string;
    };
}) {
    const { otsPartIds, genericPartIds, allComponentIds, customPartIds } = React.useMemo(() => {
        const partSpecifications = designItems?.map((item) => item.part_specification);
        const otsPartIds = (partSpecifications ?? []).flatMap((spec) =>
            isPresent(spec) ? getApprovedOtsPartIds(spec) : [],
        );
        const genericPartIds = (partSpecifications ?? []).flatMap((spec) =>
            isPresent(spec) ? getApprovedGenericPartIds(spec) : [],
        );
        const allComponentIds = (partSpecifications ?? []).flatMap((spec) =>
            isPresent(spec) ? [...getApprovedIpnIds(spec), ...getCustomApprovedIpnIds(spec)] : [],
        );
        const customPartIds = (partSpecifications ?? []).flatMap((spec) =>
            isPresent(spec) ? getApprovedCustomPartIds(spec) : [],
        );
        return { otsPartIds, genericPartIds, allComponentIds, customPartIds };
    }, [designItems]);

    const { data: dataOtsParts = [], isLoading: isLoadingOtsParts } = useOtsPartBulk(otsPartIds, rfqContext);
    const { data: dataGenericParts = [], isLoading: isLoadingGenericParts } = useGenericPartBulk(
        genericPartIds,
        rfqContext,
    );
    const { data: dataComponents = [], isLoading: isLoadingComponents } = useComponentsBulk(
        allComponentIds,
        rfqContext,
    );
    const { data: dataCustomParts = [], isLoading: isLoadingCustomParts } = useCustomPartsBulk(customPartIds);

    const isLoading = isLoadingGenericParts || isLoadingOtsParts || isLoadingCustomParts || isLoadingComponents;

    return React.useMemo(() => {
        if (!isPresent(designItems) || isLoading) {
            return { data: undefined, isLoading: true };
        }

        return {
            data: [
                ...dataOtsParts,
                ...dataGenericParts,
                ...dataCustomParts,
                ...dataComponents.map((component) => component.data),
            ],
            isLoading,
        };
    }, [designItems, dataOtsParts, dataGenericParts, dataComponents, dataCustomParts, isLoading]);
}

export function useCustomPartTypes(ignorePcbType: boolean, enabled: boolean = true) {
    return useHttpQuery(
        'GET /parts/custom-types',
        {},
        {
            enabled,
            select: (data) => {
                if (ignorePcbType) {
                    return data.items.filter((type) => type !== CustomPartTypeEnum.PCB);
                }
                return data.items;
            },
        },
    );
}

export function usePartPackages(tags?: PackageTag | undefined) {
    return useSuspenseHttpQuery(
        'GET /parts/packages',
        { queryParams: isPresent(tags) ? { tags } : {} },
        {
            select: (res) => Array.from(res.items).sort((a, b) => compareByString(a.name ?? '', b.name ?? '')),
            staleTime: Infinity,
        },
    );
}

/**
 * Returns the ApprovalStatus of the part option with the given ID. If no such partId is found, then returns undefined.
 */
function getApprovalStatus(spec: PartSpecification, partId: string): ApprovalStatus | undefined {
    if (spec.type === PartSpecificationTypes.Custom) {
        return spec?.data.find((p) => p.part.data === partId)?.approval_status;
    }
    if (spec.type === PartSpecificationTypes.OffTheShelf) {
        return spec.data.part_options.find((p) => p.part.data === partId)?.approval_status;
    }
    assertUnreachable(spec);
}

/**
 * Takes an array of full parts as input and a part specification
 * and returns a match of all full parts that satisfy the part specification alongside the
 * approval status
 */
export const getFullPartsWithApprovalStatus = (
    specification: PartSpecification | null,
    partsMap: Map<string, FullPart>,
): FullPartWithApprovalStatus[] => {
    if (!specification) {
        return [];
    }

    const partIds = getPartIdsSet(specification, { filterByStatus: false });

    return Array.from(partIds)
        .map((partId) => partsMap.get(partId))
        .filter(isPresent)
        .map((part: FullPart): FullPartWithApprovalStatus => {
            const approvalStatus = getApprovalStatus(specification, part.id)!;
            if (isGenericFullPart(part)) {
                return {
                    type: 'Generic',
                    approvalStatus,
                    part,
                };
            }
            if (isCustomFullPart(part)) {
                return {
                    type: 'Custom',
                    approvalStatus,
                    part,
                };
            }
            if (isCustomComponentFull(part)) {
                return {
                    type: 'CustomComponent',
                    approvalStatus,
                    part,
                };
            }
            if (isOtsFullPart(part)) {
                return {
                    type: 'OffTheShelf',
                    approvalStatus,
                    part,
                };
            }
            if (isOtsComponentFull(part)) {
                return {
                    type: 'Ipn',
                    approvalStatus,
                    part,
                };
            }

            return assertUnreachable(part);
        })
        .filter((p) => !!p.approvalStatus);
};

export function useOtsPartByMpn({ mpns, rfqContext }: { mpns: string[]; rfqContext: RfqContext }) {
    return useHttpQuery(
        'POST /parts/off-the-shelf/search/mpn/bulk',
        {
            requestBody: {
                items: mpns,
                use_elastic: true,
                search_external_apis: false,
                size: 200,
                ...convertRfqContext(rfqContext),
            },
        },
        {
            select: (res) => Object.entries(res.data).map(([key, value]) => ({ alternativeMpn: key, matches: value })),
        },
    );
}

export function usePcbDetailsOfOffer(offers: CustomOptionOfferDTO[] | undefined) {
    const pcbIds = React.useMemo(() => {
        return offers
            ?.filter((offer) => isCustomPartOffer(offer))
            .map((offer) => offer.specification?.data.pcb)
            .filter((id) => isPresent(id));
    }, [offers]);

    const uniquePcbIds = pcbIds ? uniq(pcbIds) : [];

    if (uniquePcbIds.length > 1) {
        throwErrorUnlessProduction(new Error(`Multiple PCBs found for the same offer: ${uniquePcbIds}`));
    }

    return usePcb(uniquePcbIds[0]);
}
